'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { Menu } from 'lucide-react';

import { withLocale } from '@/lib/withLocale';
import { MegaMenu } from '../mega-menu';
import { NavLinks } from './NavLinks';
import { LanguageSwitcher } from './LanguageSwitcher';
import Search from './Search';
import { SUPPORTED, LangType, Locale } from './constants';

export default function Header() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname() || '/';

  // Derive current locale from pathname
  const currentLocale: LangType = (() => {
    const seg = pathname.split('/')[1]?.toLowerCase();
    return SUPPORTED.includes(seg as LangType) ? (seg as LangType) : Locale.ID; // fallback
  })();

  return (
    <header className="sticky top-0 z-50 bg-white shadow-lg">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-4 sm:px-6 md:h-20 md:px-10">
        {/* Left: Hamburger + Logo */}
        <div className="flex min-w-0 items-center gap-2 md:gap-3">
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="shrink-0 rounded p-1 hover:bg-gray-100"
          >
            <Menu strokeWidth="3px" />
          </button>

          <Link
            href={withLocale('/', currentLocale)}
            className="shrink-0 text-2xl font-bold text-gray-800"
          >
            {/* Logo wrapper: width fixed, height auto via aspect (image keeps ratio) */}
            <span className="relative inline-block w-12 h-12 align-middle md:w-[60px] md:h-[60px]">
            <Image
              src="/images/icon.png"
              alt="SMF logomark"
              fill
              className="object-contain"
              priority
              sizes="60px"
            />
            </span>
          </Link>
        </div>

        {/* Right: Nav + Language */}
        <div className="flex min-w-0 items-center gap-2 md:gap-10">
          <NavLinks />
          <Search />
          <LanguageSwitcher />
        </div>
      </div>

      {/* Mega menu overlay */}
      <MegaMenu open={open} setOpen={setOpen} />
    </header>
  );
}